<template>
    <section class="mc-card stampa-odr-group" :aria-label="group.name || 'Fase non indicata'">
        <div class="mc-card__head stampa-odr-group__head">
            <div class="stampa-odr-group__title">
                <h2 class="mc-card__title">{{ group.name || 'Fase non indicata' }}</h2>
                <span class="mc-chip mc-num">{{ displayNumber(group.orders.length) }}</span>
            </div>
            <v-btn
                small
                outlined
                :loading="printing === 'batch'"
                :disabled="localPreview || !printerId || !printableOrders.length || Boolean(printing)"
                @click="$emit('print-group')"
            >
                <v-icon small left>mdi-printer-multiple</v-icon>
                Stampa tutti ({{ displayNumber(printableOrders.length) }})
            </v-btn>
        </div>
        <div class="mc-scroll-x stampa-odr-group__table-wrap">
            <table class="mc-table stampa-odr-table" role="table" :aria-label="'ODR · ' + (group.name || 'Fase non indicata')">
                <thead role="rowgroup">
                    <tr role="row">
                        <th v-for="label in labels" :key="label" scope="col" role="columnheader">{{ label }}</th>
                    </tr>
                </thead>
                <tbody role="rowgroup">
                    <tr v-for="order in group.orders" :key="order.ordine" role="row">
                        <td role="cell">
                            <span class="stampa-odr-mobile-label" aria-hidden="true">ODR</span>
                            <router-link :to="{ name: 'Ordine', params: { codice: order.ordine } }" class="mc-code stampa-odr-link">
                                {{ order.ordine }}
                            </router-link>
                        </td>
                        <td role="cell"><span class="stampa-odr-mobile-label" aria-hidden="true">Articolo</span><span class="mc-code">{{ order.articolo || '—' }}</span></td>
                        <td role="cell"><span class="stampa-odr-mobile-label" aria-hidden="true">Riferimento</span>{{ order.rif || '—' }}</td>
                        <td role="cell"><span class="stampa-odr-mobile-label" aria-hidden="true">Data inizio</span>{{ displayDate(order.data_inizio) }}</td>
                        <td role="cell"><span class="stampa-odr-mobile-label" aria-hidden="true">Cliente</span>{{ order.cliente_nome || '—' }}</td>
                        <td role="cell">
                            <span class="stampa-odr-mobile-label" aria-hidden="true">Disegno / stato</span>
                            <div>
                                <div v-if="order.disegno" class="stampa-odr-drawing">{{ order.disegno }}</div>
                                <span v-else class="stampa-odr-missing">Disegno mancante</span>
                                <div v-if="order.in_coda" class="t-caption stampa-odr-queued">Stampa in coda</div>
                                <div v-else-if="order.schedulato !== '2'" class="t-caption stampa-odr-warning">Non ancora schedulato</div>
                                <div v-else-if="Number(order.stampabile) !== 2" class="t-caption stampa-odr-warning">
                                    {{ order.stampabile_msg || 'ODR non ancora stampabile' }}
                                </div>
                            </div>
                        </td>
                        <td role="cell" class="stampa-odr-action">
                            <span class="stampa-odr-mobile-label" aria-hidden="true">Azione</span>
                            <mc-status-chip v-if="order.in_coda" tone="good" icon="check" label="In coda" />
                            <div v-else-if="needsPolling(order)" class="stampa-odr-waiting">
                                <v-progress-circular
                                    indeterminate
                                    size="16"
                                    width="2"
                                    :aria-label="order.ordine + ' in attesa di stampabilità'"
                                />
                                <mc-status-chip tone="warn" icon="clock" label="In attesa" />
                            </div>
                            <v-btn
                                v-else
                                small
                                depressed
                                color="primary"
                                :loading="printing === order.ordine"
                                :disabled="localPreview || !printerId || !isPrintable(order) || Boolean(printing)"
                                @click="$emit('print-order', order)"
                            >
                                <v-icon small left>mdi-printer</v-icon>Stampa
                            </v-btn>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </section>
</template>

<script>
import McStatusChip from "@/assets/mecom/vue/McStatusChip.vue";
import { date, number } from "@/assets/mecom/js/format.mjs";
import { isStampaOdrPrintable, needsStampaOdrPolling } from "../stampaOdrStatus.mjs";

export default {
    name: "StampaOdrGroup",
    components: { McStatusChip },
    props: {
        group: { type: Object, required: true },
        printerId: { type: [String, Number], default: null },
        localPreview: { type: Boolean, default: false },
        printing: { type: String, default: null },
    },
    data: () => ({
        labels: ["ODR", "Articolo", "Riferimento", "Data inizio", "Cliente", "Disegno / stato", "Azione"],
    }),
    computed: {
        printableOrders() { return this.group.orders.filter(isStampaOdrPrintable); },
    },
    methods: {
        displayDate: date,
        displayNumber: number,
        isPrintable: isStampaOdrPrintable,
        needsPolling: needsStampaOdrPolling,
    },
};
</script>

<style scoped>
.stampa-odr-group { padding: 0; overflow: hidden; }
.stampa-odr-group__head { align-items: center; margin: 0; padding: var(--space-4) var(--space-5); background: var(--surface-2); border-bottom: 1px solid var(--border); }
.stampa-odr-group__title { display: flex; align-items: center; flex-wrap: wrap; gap: var(--space-2); min-width: 0; }
.stampa-odr-group__title h2 { overflow-wrap: anywhere; color: var(--ink); }
.stampa-odr-table { table-layout: fixed; }
.stampa-odr-table th:first-child { width: 14%; }
.stampa-odr-table th:nth-child(4) { width: 11%; }
.stampa-odr-table th:nth-child(6) { width: 19%; }
.stampa-odr-table th:last-child { width: 14%; }
.stampa-odr-table td { overflow-wrap: anywhere; }
.stampa-odr-table tbody tr:last-child td { border-bottom: 0; }
.stampa-odr-link { display: inline-flex; align-items: center; min-height: var(--touch-target); color: var(--brand-ink); }
.stampa-odr-drawing { overflow-wrap: anywhere; }
.stampa-odr-warning, .stampa-odr-waiting { color: var(--warn-ink); }
.stampa-odr-missing { color: var(--bad-ink); }
.stampa-odr-queued { color: var(--good-ink); }
.stampa-odr-waiting { display: flex; align-items: center; flex-wrap: wrap; gap: var(--space-2); }
.stampa-odr-action .v-btn { min-height: var(--touch-target); }
.stampa-odr-action .v-btn.primary { background: var(--brand) !important; color: var(--on-brand) !important; }
.stampa-odr-mobile-label { display: none; }
@media (max-width: 900px) {
    .stampa-odr-group__head { padding: var(--space-4); }
    .stampa-odr-table, .stampa-odr-table tbody { display: block; }
    .stampa-odr-table thead { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }
    .stampa-odr-table tr { display: grid; grid-template-columns: minmax(0, 1fr); padding: var(--space-4); border-bottom: 1px solid var(--border); }
    .stampa-odr-table tr:last-child { border-bottom: 0; }
    .stampa-odr-table td { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 2fr); align-items: baseline; gap: var(--space-3); border: 0; padding: var(--space-2) 0; }
    .stampa-odr-mobile-label { display: block; color: var(--ink-2); font-weight: 500; }
    .stampa-odr-table td > * { min-width: 0; }
    .stampa-odr-action { align-items: center; }
    .stampa-odr-action > .v-btn, .stampa-odr-action > .mc-status { justify-self: start; }
}
</style>
